import { useState, useEffect } from 'react';
import { Search, Eye, CheckCircle, Clock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { inquiryService } from '@/services/storage';
import type { Inquiry } from '@/types';
import { toast } from 'sonner';

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    setInquiries(await inquiryService.getAll());
  };

  const handleView = async (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setDetailDialogOpen(true);
  };

  const handleStatusChange = async (id: string, status: Inquiry['status']) => {
    await inquiryService.update(id, { status });
    toast.success('状态已更新');
    loadInquiries();
    if (selectedInquiry && selectedInquiry.id === id) {
      setSelectedInquiry({ ...selectedInquiry, status });
    }
  };

  const filteredInquiries = inquiries
    .filter((inquiry) => {
      const matchesSearch =
        inquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inquiry.productModel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inquiry.company?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter ? inquiry.status === statusFilter : true;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const getStatusBadge = (status: Inquiry['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">待处理</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500">处理中</Badge>;
      case 'completed':
        return <Badge className="bg-green-500">已完成</Badge>;
      default:
        return <Badge>未知</Badge>;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">询单管理</h1>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">全部询单</p>
            <p className="text-2xl font-bold">{inquiries.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">待处理</p>
            <p className="text-2xl font-bold">
              {inquiries.filter((i) => i.status === 'pending').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">处理中</p>
            <p className="text-2xl font-bold">
              {inquiries.filter((i) => i.status === 'processing').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">已完成</p>
            <p className="text-2xl font-bold">
              {inquiries.filter((i) => i.status === 'completed').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="搜索客户姓名、产品型号..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-40">
              <select
                className="w-full border rounded-md px-3 py-2"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">全部状态</option>
                <option value="pending">待处理</option>
                <option value="processing">处理中</option>
                <option value="completed">已完成</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inquiries Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>客户姓名</TableHead>
                <TableHead>产品型号</TableHead>
                <TableHead>公司</TableHead>
                <TableHead>提交时间</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInquiries.map((inquiry) => (
                <TableRow key={inquiry.id}>
                  <TableCell className="font-medium">{inquiry.name}</TableCell>
                  <TableCell>{inquiry.productModel}</TableCell>
                  <TableCell>{inquiry.company || '-'}</TableCell>
                  <TableCell>
                    {new Date(inquiry.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{getStatusBadge(inquiry.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleView(inquiry)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredInquiries.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无询单数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>询单详情</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">当前状态</p>
                  <div className="mt-1">{getStatusBadge(selectedInquiry.status)}</div>
                </div>
                <div className="flex space-x-2">
                  {selectedInquiry.status !== 'processing' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(selectedInquiry.id, 'processing')}
                    >
                      <Clock className="mr-1 h-4 w-4" />
                      标记为处理中
                    </Button>
                  )}
                  {selectedInquiry.status !== 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(selectedInquiry.id, 'completed')}
                    >
                      <CheckCircle className="mr-1 h-4 w-4" />
                      标记为已完成
                    </Button>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-bold mb-3">联系信息</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">姓名</p>
                    <p className="font-medium">{selectedInquiry.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">电话</p>
                    <p className="font-medium">{selectedInquiry.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">邮箱</p>
                    <p className="font-medium">{selectedInquiry.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">公司</p>
                    <p className="font-medium">{selectedInquiry.company || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">地区</p>
                    <p className="font-medium">
                      {selectedInquiry.province || '-'}{' '}
                      {selectedInquiry.city || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div>
                <h3 className="font-bold mb-3">产品需求</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">产品型号</p>
                    <p className="font-medium">{selectedInquiry.productModel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">数量</p>
                    <p className="font-medium">{selectedInquiry.quantity || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">预估用量</p>
                    <p className="font-medium">{selectedInquiry.estimatedUsage || '-'}</p>
                  </div>
                </div>
                {selectedInquiry.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-500">详细描述</p>
                    <p className="mt-1 p-3 bg-gray-50 rounded-lg">
                      {selectedInquiry.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(`mailto:${selectedInquiry.email}`)}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  发送邮件
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
